import type { MouseEvent, ReactNode } from "react";
import { cx } from "../presentation.tsx";
import { Icon, type IconName } from "../icons.tsx";

export interface ListGroupProps {
  /** Section heading above the group. */
  title?: ReactNode;
  description?: ReactNode;
  /** Heading-right action, e.g. an "Add" link. */
  action?: ReactNode;
  /** A dot before the title that repeats the group's state ("Called to the door"). */
  tone?: "brand" | "accent" | "success" | "info" | "warning" | "neutral";
  children?: ReactNode;
  className?: string;
  id?: string;
}

/** An inset, grouped list: rows on one rounded surface with hairlines between them. */
export function ListGroup({ title, description, action, tone, children, className, id }: ListGroupProps) {
  const headingId = id ? `${id}-title` : undefined;
  return (
    <section className={cx("pmu-lgroup", className)} aria-labelledby={title ? headingId : undefined} id={id}>
      {(title || action) && (
        <header className="pmu-lgroup__header">
          <div className="pmu-lgroup__heading">
            {title && <h2 className="pmu-lgroup__title" id={headingId}>{tone && <span className={cx("pmu-lgroup__dot", `pmu-lgroup__dot--${tone}`)} aria-hidden="true" />}{title}</h2>}
            {description && <p className="pmu-lgroup__description">{description}</p>}
          </div>
          {action && <div className="pmu-lgroup__action">{action}</div>}
        </header>
      )}
      <ul className="pmu-lgroup__list">{children}</ul>
    </section>
  );
}

export interface ListRowProps {
  /** Avatar, or an icon name drawn in a tinted circle. */
  leading?: ReactNode | { icon: IconName; tone?: "brand" | "accent" | "success" | "info" | "warning" | "neutral" };
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right side: a badge, a time, a button. */
  trailing?: ReactNode;
  /** With href the row is a link, with onClick a button; otherwise static. */
  href?: string;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  /** Shows a chevron; defaults to true for links and buttons. */
  chevron?: boolean;
  /** Accessible name when the visible text is not enough. */
  "aria-label"?: string;
  className?: string;
}

export function ListRow({ leading, title, subtitle, trailing, href, onClick, chevron, className, ...rest }: ListRowProps) {
  const interactive = Boolean(href || onClick);
  const lead = leading && typeof leading === "object" && "icon" in (leading as object)
    ? (() => { const l = leading as { icon: IconName; tone?: string }; return <span className={cx("pmu-lrow__icon", `pmu-lrow__icon--${l.tone ?? "brand"}`)}><Icon name={l.icon} /></span>; })()
    : leading as ReactNode;
  const body = (
    <>
      {lead && <span className="pmu-lrow__leading">{lead}</span>}
      <span className="pmu-lrow__text">
        <span className="pmu-lrow__title">{title}</span>
        {subtitle && <span className="pmu-lrow__subtitle">{subtitle}</span>}
      </span>
      {trailing && <span className="pmu-lrow__trailing">{trailing}</span>}
      {(chevron ?? interactive) && <Icon name="chevron-right" className="pmu-lrow__chevron" />}
    </>
  );
  const cls = cx("pmu-lrow", interactive && "pmu-lrow--interactive", className);
  return (
    <li className="pmu-lgroup__item">
      {href ? <a className={cls} href={href} onClick={onClick} aria-label={rest["aria-label"]}>{body}</a>
        : onClick ? <button type="button" className={cls} onClick={onClick} aria-label={rest["aria-label"]}>{body}</button>
        : <div className={cls}>{body}</div>}
    </li>
  );
}

export interface TimelineItemProps {
  icon: IconName;
  tone?: "brand" | "accent" | "success" | "info" | "warning" | "neutral";
  title: ReactNode;
  /** Right-aligned time, e.g. "3:02 PM". */
  time?: ReactNode;
  children?: ReactNode;
  href?: string;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
}

/** A vertical timeline: a hairline with an icon per event, newest first. */
export function Timeline({ children, className, label }: { children?: ReactNode; className?: string; label?: string }) {
  return <ol className={cx("pmu-timeline", className)} aria-label={label}>{children}</ol>;
}

export function TimelineItem({ icon, tone = "brand", title, time, children, href, onClick }: TimelineItemProps) {
  const body = (
    <>
      <span className={cx("pmu-timeline__icon", `pmu-lrow__icon--${tone}`)} aria-hidden="true"><Icon name={icon} /></span>
      <span className="pmu-timeline__body">
        <span className="pmu-timeline__title">{title}</span>
        {children && <span className="pmu-timeline__detail">{children}</span>}
      </span>
      {time && <span className="pmu-timeline__time">{time}</span>}
      {href && <Icon name="chevron-right" className="pmu-lrow__chevron" />}
    </>
  );
  return (
    <li className="pmu-timeline__item">
      {href ? <a className="pmu-timeline__row pmu-lrow--interactive" href={href} onClick={onClick}>{body}</a> : <div className="pmu-timeline__row">{body}</div>}
    </li>
  );
}
